import jsPDF from 'jspdf';

class PdfService {
  async generatePdf(htmlContent, options = {}) {
    try {
      // Create PDF with text content instead of image
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Parse HTML content to extract text and structure
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      
      // Set up PDF styling
      pdf.setFont('helvetica');
      let yPosition = 10;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pageWidth - (margin * 2);

      // Add header information with logo
      const logoImg = doc.querySelector('img');
      if (logoImg && logoImg.src) {
        try {
          // Convert image to base64 and add to PDF
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const img = new Image();
          img.crossOrigin = 'anonymous';
          
          await new Promise((resolve, reject) => {
            img.onload = () => {
              canvas.width = 60;
              canvas.height = 60;
              ctx.drawImage(img, 0, 0, 60, 60);
              const imgData = canvas.toDataURL('image/png');
              pdf.addImage(imgData, 'PNG', margin, 10, 20, 20);
              resolve();
            };
            img.onerror = () => resolve(); // Continue without logo if fails
            img.src = logoImg.src;
          });
        } catch (error) {
          console.warn('Logo loading failed:', error);
        }
      }
      
      // Company info in middle column - vertically centered with logo
      const companyStartX = margin + 30;
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Hera Solutions, Inc', companyStartX, 18);
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('4062 Peachtree Rd NE', companyStartX, 24);
      pdf.text('STE A556', companyStartX, 28);
      pdf.text('Atlanta, GA 30319', companyStartX, 32);
      pdf.text('www.hera.app', companyStartX, 36);

      // Extract bill-to information
      const billToInfo = doc.querySelector('.bill-to-info');
      if (billToInfo) {
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Bill To', pageWidth - margin - 60, 18);
        
        const billToTexts = billToInfo.querySelectorAll('p');
        let billYPos = 24;
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        
        billToTexts.forEach(p => {
          const text = p.textContent.trim();
          if (text) {
            pdf.text(text, pageWidth - margin - 60, billYPos);
            billYPos += 4;
          }
        });
      }
      
      yPosition = 45;

      // Extract and add table data
      const tables = doc.querySelectorAll('.invoice-table');
      
      tables.forEach((table, tableIndex) => {
        if (tableIndex > 0) {
          pdf.addPage();
          yPosition = 10;
        }
        
        // Add company name for each section
        const companySection = table.closest('.company-section') || table.previousElementSibling;
        if (companySection) {
          const companyName = companySection.textContent.trim();
          if (companyName) {
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text(companyName, margin, yPosition);
            yPosition += 10;
          }
        }
        
        // Table headers
        const headers = Array.from(table.querySelectorAll('thead th')).map(th => th.textContent.trim());
        const allRows = Array.from(table.querySelectorAll('tbody tr')).map(tr => 
          Array.from(tr.querySelectorAll('td')).map(td => td.textContent.trim())
        );
        
        // Filter out completely empty rows
        const rows = allRows.filter(row => 
          row.some(cell => cell && cell.trim() !== '')
        );
        
        // Track actual data rows for alternating backgrounds
        let dataRowIndex = 0;
        
        // Calculate column widths dynamically based on visible columns
        const visibleColumnCount = headers.length;
        const baseColWidth = contentWidth / visibleColumnCount;
        
        // Adjust widths: Day and Active Staff slightly smaller, others equal
        const dayColWidth = baseColWidth * 0.8;
        const activeStaffColWidth = baseColWidth * 0.8;
        const serviceColWidth = (contentWidth - dayColWidth - activeStaffColWidth) / (visibleColumnCount - 2);
        
        // Draw table headers with proper widths
        pdf.setFillColor(248, 249, 250);
        pdf.rect(margin, yPosition - 5, contentWidth, 8, 'F');
        
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        
        let currentX = margin;
        headers.forEach((header, i) => {
          let colWidth;
          if (i === 0) colWidth = dayColWidth;
          else if (i === 1) colWidth = activeStaffColWidth;
          else colWidth = serviceColWidth;
          
          const align = i === 0 ? 'left' : i === 1 ? 'center' : 'right';
          const textX = align === 'right' ? currentX + colWidth - 2 : 
                       align === 'center' ? currentX + colWidth / 2 : 
                       currentX + 2;
          
          // Handle text wrapping for long headers
          const maxWidth = colWidth - 4; // Leave some padding
          const words = header.split(' ');
          
          if (pdf.getTextWidth(header) > maxWidth && words.length > 1) {
            // Split into multiple lines
            let line1 = words[0];
            let line2 = words.slice(1).join(' ');
            
            // Adjust if first line is still too long
            if (pdf.getTextWidth(line1) > maxWidth) {
              line1 = header.substring(0, 12)
              line2 = '';
            }
            
            pdf.text(line1, textX, yPosition - 2, { align });
            if (line2) {
              pdf.text(line2, textX, yPosition + 2, { align });
            }
          } else {
            // Single line - truncate if too long
            let displayText = header;
            if (pdf.getTextWidth(header) > maxWidth) {
              displayText = header.substring(0, 12);
            }
            pdf.text(displayText, textX, yPosition, { align });
          }
          
          currentX += colWidth;
        });
        yPosition += 10;
        
        // Draw table rows
        pdf.setFont('helvetica', 'normal');
        rows.forEach((row, rowIndex) => {
          if (yPosition > pageHeight - 20) {
            pdf.addPage();
            yPosition = 10;
          }
          
          // Check if this is a subtotal row
          const isSubtotalRow = row[0] && row[0].includes('Subtotal');
          
          if (isSubtotalRow) {
            // Special handling for subtotal row
            pdf.setFillColor(248, 249, 250);
            pdf.rect(margin, yPosition - 4, contentWidth, 6, 'F');
            pdf.setFont('helvetica', 'bold');
            
            // Draw "Subtotal:" spanning first two columns
            pdf.text('Subtotal:', margin + 2, yPosition);
            
            // Draw subtotal values in their respective columns
            let subtotalX = margin + dayColWidth + activeStaffColWidth;
            for (let i = 1; i < row.length; i++) {
              if (row[i] && row[i] !== '') {
                pdf.text(row[i], subtotalX + serviceColWidth - 2, yPosition, { align: 'right' });
              }
              subtotalX += serviceColWidth;
            }
          } else {
            // Regular row handling with alternating backgrounds
            const isDataRow = row.some(cell => cell && cell !== '—' && cell.trim() !== '');
            
            if (isDataRow && dataRowIndex % 2 === 1) {
              pdf.setFillColor(250, 251, 252);
              pdf.rect(margin, yPosition - 4, contentWidth, 6, 'F');
            }
            
            if (isDataRow) dataRowIndex++;
            
            let rowX = margin;
            row.forEach((cell, i) => {
              let colWidth;
              if (i === 0) colWidth = dayColWidth;
              else if (i === 1) colWidth = activeStaffColWidth;
              else colWidth = serviceColWidth;
              
              const align = i === 0 ? 'left' : i === 1 ? 'center' : 'right';
              const textX = align === 'right' ? rowX + colWidth - 2 : 
                           align === 'center' ? rowX + colWidth / 2 : 
                           rowX + 2;
              
              // Handle trial text in day column
              if (i === 0 && cell.includes('Trial')) {
                const parts = cell.split('Trial');
                const dayPart = parts[0].trim();
                pdf.setTextColor(0, 0, 0);
                pdf.text(dayPart, textX, yPosition, { align });
                
                // Add trial text inline
                const dayWidth = pdf.getTextWidth(dayPart + ' ');
                pdf.setTextColor(0, 102, 204);
                pdf.setFont('helvetica', 'bold');
                pdf.setFontSize(7);
                pdf.text('Trial', textX + dayWidth, yPosition);
                pdf.setTextColor(0, 0, 0);
                pdf.setFont('helvetica', 'normal');
                pdf.setFontSize(9);
              } else {
                pdf.text(cell, textX, yPosition, { align });
              }
              
              rowX += colWidth;
            });
          }
          yPosition += 6;
        });
        
        yPosition += 10;
      });
      
      // Add summary section
      const summarySection = doc.querySelector('.summary-section');
      if (summarySection) {
        const summaryRows = summarySection.querySelectorAll('.summary-row');
        const summaryX = pageWidth - margin - 80;
        
        pdf.setFontSize(10);
        summaryRows.forEach(row => {
          if (yPosition > pageHeight - 15) {
            pdf.addPage();
            yPosition = 10;
          }
          
          const label = row.querySelector('.summary-label')?.textContent.trim() || '';
          const value = row.querySelector('.summary-value')?.textContent.trim() || '';
          
          if (row.classList.contains('total')) {
            pdf.setFillColor(0, 102, 204);
            pdf.rect(summaryX - 5, yPosition - 4, 85, 8, 'F');
            pdf.setTextColor(255, 255, 255);
            pdf.setFont('helvetica', 'bold');
          } else {
            pdf.setTextColor(0, 0, 0);
            pdf.setFont('helvetica', 'normal');
          }
          
          pdf.text(label, summaryX, yPosition);
          pdf.text(value, summaryX + 75, yPosition, { align: 'right' });
          yPosition += 8;
        });
      }

      return pdf;
    } catch (error) {
      console.error('PDF generation error:', error);
      throw error;
    }
  }
}

export default new PdfService();